"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSearchAPI } from "./useSearchAPI";
import {
  detectSearchType,
  isValidEmail,
  isValidLinkedIn,
  validateSearchInput,
  isSearchValid,
  isValidName,
} from "../utils/searchValidators";
import { getSmartSuggestion } from "../utils/searchSuggestions";
import { findSpecialProfileRedirect } from "../constants/specialProfileBypass";
import {
  hasDisplayCompany,
  toRawCandidateFromSearchResult,
} from "@/lib/search/rawCandidates";

const candidateVanity = (candidate) =>
  candidate?.linkedinUrl
    ?.match(/linkedin\.com\/in\/([^/?#]+)/i)?.[1]
    ?.toLowerCase() || "";

const candidateNameCompany = (candidate) =>
  [candidate?.firstName, candidate?.lastName, candidate?.company]
    .map((part) =>
      String(part || "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

// RC repeats profiles across cursor pages, and a DB candidate already in the
// table can come back from RC under its real job title. Two people with the
// same name at the same company are only treated as distinct when both carry a
// LinkedIn slug to tell them apart.
function dedupeAgainstExisting(existing, incoming) {
  const seenVanities = new Set();
  const seenNameCompany = new Set();
  for (const candidate of existing) {
    const vanity = candidateVanity(candidate);
    if (vanity) seenVanities.add(vanity);
    else seenNameCompany.add(candidateNameCompany(candidate));
  }

  return incoming.filter((candidate) => {
    const vanity = candidateVanity(candidate);
    const nameCompany = candidateNameCompany(candidate);
    if (vanity && seenVanities.has(vanity)) return false;
    if (seenNameCompany.has(nameCompany)) return false;
    if (vanity) seenVanities.add(vanity);
    else seenNameCompany.add(nameCompany);
    return true;
  });
}

export const useSearch = () => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [preventSync, setPreventSync] = useState(false);
  const [isNameOnlySearch, setIsNameOnlySearch] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [skeletonMode, setSkeletonMode] = useState("profile");
  const [tableSkeletonReady, setTableSkeletonReady] = useState(false);
  const [moreResults, setMoreResults] = useState([]);
  const [rawCandidates, setRawCandidates] = useState([]);
  const [rcPage, setRcPage] = useState(1);
  const [rcCursor, setRcCursor] = useState(null);
  const [rcSearchFilters, setRcSearchFilters] = useState(null);
  const [rcTotalCount, setRcTotalCount] = useState(null);
  const [hasLoadedRCCandidates, setHasLoadedRCCandidates] = useState(false);
  const [isLoadingMoreRC, setIsLoadingMoreRC] = useState(false);
  const [enrichingId, setEnrichingId] = useState(null);
  const [enrichedUrls, setEnrichedUrls] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionLabel, setSuggestionLabel] = useState("");
  const [showTableNarrowHint, setShowTableNarrowHint] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [externalSearchUnavailable, setExternalSearchUnavailable] = useState(
    false,
  );

  const [nameQuery, setNameQuery] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");

  const inputRef = useRef(null);
  const focusSearchInputRef = useRef(false);

  useEffect(() => {
    if (!focusSearchInputRef.current || showResults || showSkeleton) return;
    const input = inputRef.current;
    if (!input) return;
    focusSearchInputRef.current = false;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [showResults, showSkeleton]);

  const {
    performSearch,
    performDiscoverSearch,
    loadMoreRCCandidates,
    performEnrichOne,
  } = useSearchAPI();

  const openProfile = (result, fresh = false) => {
    const profileId = result.url || result.id;
    if (!profileId) return false;
    const path = `/profile/${encodeURIComponent(profileId)}`;
    // A prior visit can cache "Not Found" before search creates this profile.
    if (fresh || result.externalEnriched) {
      window.location.assign(path);
    } else {
      router.push(path);
    }
    return true;
  };

  const updateRCPagination = (searchResponse = {}) => {
    const filters = searchResponse.rcSearchFilters || null;
    setRcCursor(searchResponse.rcNextCursor || null);
    setRcSearchFilters(filters);
    if (searchResponse.rcTotalCount != null) {
      setRcTotalCount(searchResponse.rcTotalCount);
    }
    setHasLoadedRCCandidates(
      Boolean(filters) && searchResponse.rcSearchDeferred !== true,
    );
  };

  const resetSearch = () => {
    focusSearchInputRef.current = true;
    setSearchQuery("");
    setNameQuery("");
    setCompanyQuery("");
    setSearchError("");
    setExternalSearchUnavailable(false);
    setResults([]);
    setShowResults(false);
    setIsLoading(false);
    setPreventSync(false);
    setIsNameOnlySearch(false);
    setMoreResults([]);
    setRawCandidates([]);
    setRcPage(1);
    setRcCursor(null);
    setRcSearchFilters(null);
    setRcTotalCount(null);
    setHasLoadedRCCandidates(false);
    setEnrichingId(null);
    setEnrichedUrls(new Set());
    setSuggestions([]);
    setSuggestionLabel("");
    setShowTableNarrowHint(false);
    setShowSkeleton(false);
    setSkeletonMode("profile");
    setTableSkeletonReady(false);
    setIsLoadingMoreRC(false);

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const refineSearch = () => {
    const currentQuery = searchQuery;
    resetSearch();
    setSearchQuery(currentQuery);
    setSearchType(detectSearchType(currentQuery));
  };

  const handleSearch = async (queryOverride = null) => {
    const effectiveQuery =
      typeof queryOverride === "string" ? queryOverride : searchQuery;

    if (!effectiveQuery.trim()) return;
    setSearchQuery(effectiveQuery);

    const directProfileId = findSpecialProfileRedirect(effectiveQuery);
    if (directProfileId) {
      router.push(`/profile/${directProfileId}`);
      return;
    }

    setSearchError("");
    setRcCursor(null);
    setRcSearchFilters(null);
    setRcTotalCount(null);
    setHasLoadedRCCandidates(false);
    const currentType = detectSearchType(effectiveQuery);
    const showResultSkeleton = (mode) => {
      setSkeletonMode(mode);
      setShowSkeleton(true);
      setTableSkeletonReady(false);
    };
    const completeTableSkeleton = async () => {
      setTableSkeletonReady(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
    };
    const completeProfileSkeleton = () =>
      new Promise((resolve) => setTimeout(resolve, 350));
    // A lone match is enriched and shown as a profile card, never a one-row table.
    // Candidates without a displayable company are hidden by the results view,
    // so the single-match check must run on the same filtered set.
    const tryShowSingleCandidate = async (candidates, queryLabel) => {
      const displayable = (candidates || []).filter(hasDisplayCompany);
      if (displayable.length !== 1 || !displayable[0].linkedinUrl) return false;

      showResultSkeleton("profile");
      const card = await performEnrichOne(displayable[0]);
      if (!card) return false;

      setNameQuery(queryLabel);
      setResults([card]);
      setMoreResults([]);
      setRawCandidates([]);
      setRcPage(1);
      setShowResults(true);
      setIsNameOnlySearch(true);
      return true;
    };

    if (currentType === "discover") {
      setIsLoading(true);
      setIsNameOnlySearch(false);
      const discoverQuery = effectiveQuery.trim();
      const isPersonLookupQuery =
        isValidName(discoverQuery) || /\s+at\s+/i.test(discoverQuery);
      let streamedRcCount = null;
      let navigating = false;
      try {
        // Keep the search form visible with its loading indicator until the
        // streamed count or final result tells us which skeleton to show.
        const searchResults = await performDiscoverSearch(discoverQuery, {
          streamRcCount: isPersonLookupQuery,
          onRcCount: (count) => {
            if (typeof count !== "number") return;

            streamedRcCount = count;
            setRcTotalCount(count);
            setNameQuery(discoverQuery);
            setIsNameOnlySearch(true);

            if (count === 0) {
              setShowSkeleton(false);
              setResults([]);
              setMoreResults([]);
              setRawCandidates([]);
              setShowResults(true);
            } else {
              setSkeletonMode(count === 1 ? "profile" : "table");
              setTableSkeletonReady(false);
              setShowSkeleton(true);
            }
          },
        });
        updateRCPagination(searchResults);
        setExternalSearchUnavailable(
          searchResults.externalSearchUnavailable === true,
        );

        if (searchResults.flagged) {
          setShowSkeleton(false);
          setIsLoading(false);
          setSearchError(
            "Please describe a role, person, or company to find professionals.",
          );
          return;
        }

        if (searchResults.queryType === "name") {
          const tr0 = searchResults.transformedResults || [];
          const rc0 = searchResults.rawCandidates || [];

          if (
            tr0.length === 0 &&
            searchResults.parsedName &&
            searchResults.parsedCompany
          ) {
            const pName = searchResults.parsedName;
            const pCompany = searchResults.parsedCompany;
            const sugg0 = searchResults.suggestions || [];
            const suggLabel0 = searchResults.suggestionLabel || "";
            setNameQuery(pName);
            setCompanyQuery(pCompany);
            setSkeletonMode("profile");
            setShowSkeleton(true);
            try {
              const rcCandidatesWithUrl = rc0.filter((c) => c.linkedinUrl);
              let tr2 = [];
              if (rcCandidatesWithUrl.length > 0) {
                const toEnrich = rcCandidatesWithUrl.slice(0, 3);
                const results = await Promise.all(
                  toEnrich.map(async (candidate) => {
                    let card = await performEnrichOne(candidate);
                    if (!card && candidate === toEnrich[0]) {
                      await new Promise((r) => setTimeout(r, 3000));
                      card = await performEnrichOne(candidate);
                    }
                    return card;
                  }),
                );
                tr2 = results.filter(Boolean);
              } else if (pName.trim().includes(" ")) {
                const enrichResults = await performSearch(
                  "company",
                  `${pName} ${pCompany}`,
                  false,
                  { nameQuery: pName, companyQuery: pCompany },
                );
                tr2 = enrichResults.transformedResults || [];
              }

              const hasCompleteProfile = (r) => {
                if (!(r.picture_custom || r.picture_cached || r.picture)) {
                  return false;
                }
                const ln = (r.family_name || "").trim();
                if (ln.length > 0 && /^[\p{L}]\.?$/u.test(ln)) return false;
                return true;
              };
              const tr2WithPic = tr2.filter(hasCompleteProfile);
              const tr2NoPic = tr2.filter((r) => !hasCompleteProfile(r));
              const noPicAsCandidates = tr2NoPic.map(
                toRawCandidateFromSearchResult,
              );

              if (
                tr2WithPic.length === 1 &&
                rc0.length <= 1 &&
                noPicAsCandidates.length === 0
              ) {
                const profileId = tr2WithPic[0].url || tr2WithPic[0].id || "";
                if (profileId) {
                  navigating = true;
                  openProfile(tr2WithPic[0]);
                  return;
                }
              }

              if (tr2WithPic.length > 0) {
                const enrichedVanities = new Set(
                  tr2.map((r) => r.vanity).filter(Boolean),
                );
                const remainingRc = rc0.filter(
                  (c) =>
                    !c.linkedinUrl ||
                    !enrichedVanities.has(
                      c.linkedinUrl.match(/linkedin\.com\/in\/([^/?#]+)/i)?.[1],
                    ),
                );
                const allRawForTable = [...noPicAsCandidates, ...remainingRc];
                setResults(tr2WithPic);
                if (allRawForTable.length > 0) {
                  setRawCandidates(allRawForTable);
                  setRcPage(1);
                }
                setShowResults(true);
              } else if (rc0.length > 0) {
                if (await tryShowSingleCandidate(rc0, pName)) return;
                const skeletonMode = rc0.length === 1 ? "profile" : "table";
                showResultSkeleton(skeletonMode);
                setResults([]);
                setRawCandidates(rc0);
                setRcPage(1);
                if (skeletonMode === "table") await completeTableSkeleton();
                setShowResults(true);
              } else {
                if (sugg0.length > 0) {
                  setSuggestions(sugg0);
                  setSuggestionLabel(suggLabel0);
                }
                setShowResults(true);
              }
            } catch (enrichErr) {
              console.error("Name+company enrichment failed:", enrichErr);
              if (rc0.length > 0) {
                const skeletonMode = rc0.length === 1 ? "profile" : "table";
                showResultSkeleton(skeletonMode);
                setResults([]);
                setRawCandidates(rc0);
                setRcPage(1);
                if (skeletonMode === "table") await completeTableSkeleton();
              } else if (sugg0.length > 0) {
                setSuggestions(sugg0);
                setSuggestionLabel(suggLabel0);
              }
              setShowResults(true);
            }
            return;
          }

          if (tr0.length === 0 && rc0.length === 0) {
            const trimmedQuery = effectiveQuery.trim();
            setShowSkeleton(false);
            setNameQuery(trimmedQuery);
            if (searchResults.externalSearchUnavailable === true) {
              setExternalSearchUnavailable(true);
            }
            // Nothing anywhere: show the empty state instead of forcing a
            // company step the user cannot usefully answer.
            setResults([]);
            setRawCandidates([]);
            setRcTotalCount(searchResults.rcTotalCount ?? null);
            setShowResults(true);
            setIsNameOnlySearch(true);
            return;
          }

          if (tr0.length === 0) {
            const shownSingle = await tryShowSingleCandidate(
              rc0,
              effectiveQuery.trim(),
            );
            if (shownSingle) return;
          }

          const skeletonMode = tr0.length > 0 ? "profile" : "table";
          showResultSkeleton(skeletonMode);
          setNameQuery(effectiveQuery.trim());
          setResults(tr0);
          setMoreResults(searchResults.moreResults || []);
          setRawCandidates(rc0);
          setShowTableNarrowHint(searchResults.showNarrowSearchHint === true);
          setRcPage(1);
          if (skeletonMode === "table") {
            await completeTableSkeleton();
          } else {
            await completeProfileSkeleton();
          }
          setShowResults(true);
          setIsNameOnlySearch(true);
          return;
        }

        const tr = searchResults.transformedResults || [];
        const mr = searchResults.moreResults || [];
        const rc = searchResults.rawCandidates || [];
        const shouldLoadInitialExternalRC =
          rc.length === 0 && searchResults.deferExternalSearch === true;

        // Broad discover searches should stay in the results view even when
        // the backend returns a single card. Redirecting here makes role/location
        // queries feel like a direct profile lookup instead of a candidate list.
        if (false && tr.length === 1 && mr.length === 0 && rc.length === 0) {
          const profileId = tr[0].url || tr[0].id || "";
          if (profileId) {
            navigating = true;
            openProfile(tr[0]);
            return;
          }
        }

        if (tr.length === 0 && !shouldLoadInitialExternalRC) {
          const shownSingle = await tryShowSingleCandidate(rc, discoverQuery);
          if (shownSingle) return;
        }

        const skeletonMode =
          isPersonLookupQuery && streamedRcCount === 1 ? "profile" : "table";
        const deferSkeletonToExternalCount =
          shouldLoadInitialExternalRC && tr.length === 0;
        if (deferSkeletonToExternalCount) {
          setSkeletonMode(skeletonMode);
          setTableSkeletonReady(false);
          setShowSkeleton(false);
        } else {
          showResultSkeleton(skeletonMode);
        }
        setResults(tr);
        setMoreResults(mr);
        setRawCandidates(tr.length > 0 ? [] : rc);
        setShowTableNarrowHint(searchResults.showNarrowSearchHint === true);
        setRcPage(1);
        if (searchResults.suggestions?.length > 0) {
          setSuggestions(searchResults.suggestions);
          setSuggestionLabel(searchResults.suggestionLabel || "");
        } else {
          setSuggestions([]);
          setSuggestionLabel("");
        }
        if (!shouldLoadInitialExternalRC) {
          if (skeletonMode === "table") await completeTableSkeleton();
          setShowResults(true);
        }

        if (shouldLoadInitialExternalRC) {
          setIsLoadingMoreRC(true);
          let shownSingleDeferred = false;
          try {
            const deferredCandidates = await loadMoreRCCandidates(
              discoverQuery,
              null,
              searchResults.rcSearchFilters || null,
              {
                onRcCount: (count) => {
                  if (typeof count !== "number") return;
                  setRcTotalCount(count);

                  if (tr.length > 0) {
                    if (count === 0) {
                      setShowSkeleton(false);
                      setShowResults(true);
                    }
                    return;
                  }

                  if (count === 0) {
                    setShowSkeleton(false);
                    setRawCandidates([]);
                    setShowResults(true);
                    return;
                  }

                  setSkeletonMode(count === 1 ? "profile" : "table");
                  setTableSkeletonReady(false);
                  setShowSkeleton(true);
                },
              },
            );
            if (!deferredCandidates.externalSearchUnavailable) {
              setHasLoadedRCCandidates(true);
              setRcCursor(deferredCandidates.nextCursor || null);
              setRcSearchFilters(deferredCandidates.searchFilters || null);
              if (deferredCandidates.totalCount != null) {
                setRcTotalCount(deferredCandidates.totalCount);
              }
            }
            if (deferredCandidates.length > 0) {
              if (tr.length === 0) {
                shownSingleDeferred = await tryShowSingleCandidate(
                  deferredCandidates,
                  discoverQuery,
                );
              }
              if (!shownSingleDeferred) {
                setRawCandidates(deferredCandidates);
                setRcPage(1);
              }
            } else if (deferredCandidates.externalSearchUnavailable) {
              setExternalSearchUnavailable(true);
            }
          } finally {
            setIsLoadingMoreRC(false);
          }
          if (shownSingleDeferred) return;
          if (skeletonMode === "table") await completeTableSkeleton();
          setShowResults(true);
        }
      } catch (error) {
        console.error("Discover search error:", error);
        setShowSkeleton(false);
      } finally {
        if (!navigating) {
          setIsLoading(false);
          setShowSkeleton(false);
        }
      }
      return;
    }

    if (!validateSearchInput(effectiveQuery, currentType)) {
      return;
    }

    setIsLoading(true);
    let skeletonTimer = null;
    setSkeletonMode("profile");
    setShowSkeleton(true);
    setIsNameOnlySearch(false);

    try {
      const refinedQuery = effectiveQuery.trim();
      const isNameOnly = false;

      const searchResults = await performSearch(
        currentType,
        refinedQuery,
        isNameOnly,
        null,
      );

      if (skeletonTimer) clearTimeout(skeletonTimer);
      const tr2 = searchResults.transformedResults || [];

      if (tr2.length === 1) {
        const profileId = tr2[0].url || tr2[0].id || "";
        if (profileId) {
          setIsLoading(false);
          openProfile(tr2[0], currentType === "linkedin");
          return;
        }
      }

      setResults(tr2);
      setIsLoading(false);
      setShowSkeleton(false);
      setShowResults(true);

      if (searchResults.nbHits === 0 && isNameOnly) {
        setIsNameOnlySearch(true);
      }
    } catch (error) {
      if (skeletonTimer) clearTimeout(skeletonTimer);
      console.error("Search error:", error);
      setSearchError(
        error.message || "Could not open this profile. Please try again.",
      );
      setIsLoading(false);
      setShowSkeleton(false);
    }
  };

  const isSearchValidFunc = () => {
    return isSearchValid(searchQuery, detectSearchType);
  };

  const handleShowMore = () => {
    if (moreResults.length > 0) {
      setResults((prev) => [...prev, ...moreResults]);
      setMoreResults([]);
    }
  };

  const handleLoadNextRCPage = async () => {
    if (isLoadingMoreRC) return;
    if (hasLoadedRCCandidates && !rcCursor) return [];
    setIsLoadingMoreRC(true);
    try {
      const nextPage = rawCandidates.length > 0 ? rcPage + 1 : 1;
      const newCandidates = await loadMoreRCCandidates(
        nameQuery || searchQuery,
        rcCursor,
        rcSearchFilters,
      );
      if (!newCandidates.externalSearchUnavailable) {
        setHasLoadedRCCandidates(true);
        setRcCursor(newCandidates.nextCursor || null);
        setRcSearchFilters(newCandidates.searchFilters || rcSearchFilters);
        if (newCandidates.totalCount != null) {
          setRcTotalCount(newCandidates.totalCount);
        }
      }
      if (newCandidates.length > 0) {
        const freshCandidates = dedupeAgainstExisting(
          rawCandidates,
          newCandidates,
        );
        if (freshCandidates.length > 0) {
          setRawCandidates((prev) => [
            ...prev,
            ...dedupeAgainstExisting(prev, freshCandidates),
          ]);
          setRcPage(nextPage);
        }
        // Caller reads length to decide whether more pages remain, so report
        // what actually reached the table, not what RC sent.
        freshCandidates.externalSearchUnavailable =
          newCandidates.externalSearchUnavailable === true;
        return freshCandidates;
      }
      return newCandidates;
    } finally {
      setIsLoadingMoreRC(false);
    }
  };

  const handleEnrichOne = (candidate, { newTab = false } = {}) => {
    const params = new URLSearchParams({
      linkedin: candidate.linkedinUrl || "",
      first: candidate.firstName || "",
      last: candidate.lastName || "",
      company: candidate.company || "",
    });
    const enrichUrl = `/search/enrich?${params.toString()}`;

    if (newTab) {
      window.open(enrichUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setEnrichingId(candidate.linkedinUrl || "candidate");
    setSkeletonMode("profile");
    setShowSkeleton(true);
    setEnrichedUrls((prev) => new Set([...prev, candidate.linkedinUrl]));
    router.push(enrichUrl);
  };

  return {
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,
    isLoading,
    showSkeleton,
    skeletonMode,
    tableSkeletonReady,
    results,
    moreResults,
    rawCandidates,
    isLoadingMoreRC,
    enrichingId,
    enrichedUrls,
    showResults,
    suggestions,
    suggestionLabel,
    showTableNarrowHint,
    rcTotalCount,
    preventSync,
    setPreventSync,
    isNameOnlySearch,
    nameQuery,
    companyQuery,
    inputRef,
    detectSearchType,
    getSmartSuggestion,
    handleSearch,
    resetSearch,
    refineSearch,
    handleShowMore,
    handleLoadNextRCPage,
    handleEnrichOne,
    isSearchValid: isSearchValidFunc,
    isValidEmail,
    isValidLinkedIn,
    searchError,
    externalSearchUnavailable,
    setSearchError,
  };
};
